/**
 * useProposalReviewFlow — the bulk-resolve review queue, end to end.
 *
 * A resolve pass no longer applies its rewrites; it records each changed
 * paragraph as a pending proposal (see the server ``section_proposals`` store).
 * This hook loads that queue and drives the writer through it one proposal at a
 * time — Approve / Reject / Revise — revealing each change in the open Word
 * document as it becomes the active card.
 *
 *   idle → loading → reviewing → (applying | revising) → reviewing → … → idle
 *
 * Approve: the server applies the paragraph to the section SSOT + reconciles the
 * ledger (``decideProposal``), then this hook mirrors the ONE approved paragraph
 * into the open Word document (same section-scoped replace the chat highlight
 * edit uses). Reject: the server marks it rejected and leaves the issue open.
 * Revise: a fresh rewrite that keeps both the original and the current proposed
 * text in context, producing a new proposal that supersedes this one.
 *
 * The active card is tracked by proposal id, so a background re-read (a pass
 * landing more changes) never swaps the card under the writer: new arrivals are
 * counted in ``newCount`` instead. Decisions can also be made by id
 * (``approveById`` / ``rejectById``) — the Issues row's inline change uses them.
 * After an approved paragraph change, ``lastApplied`` holds it for
 * :data:`UNDO_MS` so ``undoLast`` can put the original back.
 *
 * A monotonic ``flowTokenRef`` guards every async settle so a stale stream or a
 * superseded decision can't resurrect a proposal the writer already moved past.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  decideProposal,
  getRunProposals,
  type ResolveIssueDoc,
  reviseProposalStream,
  type SectionProposal,
  undoProposal,
} from "../../api/client";
import { needsFormatterRender } from "../../services/formatter";
import { enqueueWordWrite } from "../../services/wordWriteQueue";
import {
  applyTableRowEditsInSection,
  replaceTextInSection,
  replaceTextInSectionStyled,
  type SectionTableRowWrite,
  selectMarkerParagraph,
  selectSpanInSection,
  updateTitlePageFields,
} from "../../services/wordWriter";
import { sanitizeDraftForWord, stripRegulatorMarkers } from "../services/draftSanitizer";
import { diffRow, parsePipeRow } from "../services/tableEdit";
import { labelForSelectionEditEvent } from "./selectionEditLabels";

/** One reviewable proposal plus the section number/title for its header +
 *  the Word write/reveal target. */
export interface ProposalQueueItem {
  proposal: SectionProposal;
  sectionNumber: string;
  sectionTitle: string;
}

export type ProposalReviewPhase = "idle" | "loading" | "reviewing" | "applying" | "revising";

/** How long an approved paragraph change can be undone from the review UI. */
export const UNDO_MS = 8_000;

/** The approved paragraph change that ``undoLast`` would reverse. */
export interface AppliedChange {
  proposalId: string;
  moduleId: string;
  sectionNumber: string;
  /** The text written into Word (sanitized), swapped back out on undo. */
  appliedText: string;
  originalText: string;
}

export interface UseProposalReviewFlowParams {
  runId: string | null;
  /** External re-fetch trigger — bumped by the shell when a resolve pass emits a
   *  proposal / finishes (``proposalsRefreshSignal``). */
  refreshSignal?: number;
  /** Active source library — scopes a Revise rewrite's retrieval. */
  libraryId?: string | null;
  /** Called after a proposal is approved or rejected so the host can refresh the
   *  Issues list (an approve resolved the issue; a reject left it open). */
  onDecided?: () => void;
  /** Surface a writer-readable error. */
  onError?: (message: string) => void;
  /** Write approved title-page fields into the open document. The host knows
   *  the doc type's cover style / template cover; defaults to a plain
   *  ``updateTitlePageFields``. */
  writeTitleFields?: (fields: Record<string, string>) => Promise<unknown>;
  /** Keep only the proposals this returns true for (the Fix window shows the
   *  changes its own pass proposed). Default: every pending proposal. */
  include?: (proposal: SectionProposal) => boolean;
  /** Re-render one section into Word from the record. Called when an approved
   *  (or undone) paragraph can't be found in the open document, so Word never
   *  stays behind the record. Omitted where the host can't place the section
   *  (a custom submission); the writer is then told to Load it. */
  reloadSectionInWord?: (moduleId: string) => Promise<void>;
}

export interface UseProposalReviewFlowResult {
  phase: ProposalReviewPhase;
  /** Every pending proposal, in submission order. */
  queue: ProposalQueueItem[];
  /** Index of the proposal under review (clamped to the queue). */
  activeIndex: number;
  /** The proposal under review, or null when the queue is empty. */
  active: ProposalQueueItem | null;
  /** Live status line for a Revise re-run (null otherwise). */
  progressLabel: string | null;
  error: string | null;
  reload: () => Promise<void>;
  next: () => void;
  prev: () => void;
  select: (index: number) => void;
  /** Make the proposal with this id the active card; false when not queued. */
  selectById: (proposalId: string) => boolean;
  /** ``content``: the writer's own edit of the proposed text, applied instead of it. */
  approve: (content?: string) => Promise<void>;
  reject: () => Promise<void>;
  revise: (guidance: string) => void;
  /** Decide a queued proposal that need not be the active card. */
  approveById: (proposalId: string, content?: string) => Promise<void>;
  rejectById: (proposalId: string) => Promise<void>;
  /** Changes that arrived since the writer last moved through the queue. */
  newCount: number;
  /** The last approved paragraph change, while it can still be undone. */
  lastApplied: AppliedChange | null;
  /** Put the original paragraph back (record + Word); the card returns. */
  undoLast: () => Promise<void>;
  /** Hide the undo offer. */
  dismissUndo: () => void;
}

/** Same folding as the server's ``section_proposals.is_noop_rewrite``: ignore
 *  whitespace, markdown emphasis, curly vs straight quotes and space before
 *  punctuation. */
function contentKey(text: string): string {
  return (text ?? "")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[*_`]+/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s+([,.;:!?)\]])/g, "$1");
}

/** A title-page field fill (old value → new value), not a paragraph rewrite. */
export function isTitleFieldProposal(proposal: SectionProposal): boolean {
  return proposal.target === "title_field" && Boolean(proposal.title_field_key);
}

/** A proposal that would change nothing in the document — an unchanged echo of
 *  the original paragraph, or a table proposal with no changed cells. Safety net
 *  for proposals stored before the server started dropping them. */
export function isNoopProposal(proposal: SectionProposal): boolean {
  if (proposal.is_table && proposal.table_row_edits?.length) {
    return tableWritesFromProposal(proposal).length === 0;
  }
  return contentKey(proposal.original_text) === contentKey(proposal.proposed_text);
}

function flattenQueue(
  sections: Awaited<ReturnType<typeof getRunProposals>>["sections"],
  include?: (proposal: SectionProposal) => boolean,
): ProposalQueueItem[] {
  const items: ProposalQueueItem[] = [];
  for (const section of sections) {
    for (const proposal of section.proposals) {
      if (proposal.status === "pending" && !isNoopProposal(proposal) && (!include || include(proposal))) {
        items.push({ proposal, sectionNumber: section.number, sectionTitle: section.title });
      }
    }
  }
  return items;
}

/** Build the section-scoped Word cell writes for an approved TABLE proposal from
 *  its per-row edits — parse each pipe row back to cells, diff to find the
 *  changed columns, and skip any row the model mangled (column-count change) or
 *  that no longer differs. Returns [] when the proposal isn't a usable table. */
function tableWritesFromProposal(proposal: SectionProposal): SectionTableRowWrite[] {
  if (!proposal.is_table || !proposal.table_row_edits?.length) return [];
  const writes: SectionTableRowWrite[] = [];
  for (const edit of proposal.table_row_edits) {
    const originalCells = parsePipeRow(edit.original_row);
    const proposedCells = parsePipeRow(edit.proposed_row);
    if (!originalCells || !proposedCells) continue;
    const diff = diffRow(originalCells, proposedCells);
    if (!diff) continue; // column count changed — never realign
    const changedColumns = diff.map((c, i) => (c.changed ? i : -1)).filter((i) => i >= 0);
    if (changedColumns.length === 0) continue;
    writes.push({ rowIndex: edit.row_index, cells: proposedCells, changedColumns, originalCells });
  }
  return writes;
}

/** A view of a shared review queue narrowed to some proposals (the Fix window
 *  shows the changes its own pass made). The active card is the shared one when
 *  it is in view, else none — the host brings one into view with
 *  ``selectById`` — and decisions go by id, so they never land on a card
 *  outside the view. */
export function filterReviewFlow(
  base: UseProposalReviewFlowResult,
  include: (proposal: SectionProposal) => boolean,
): UseProposalReviewFlowResult {
  const queue = base.queue.filter((q) => include(q.proposal));
  const active = base.active && include(base.active.proposal) ? base.active : null;
  const activeIndex = active ? queue.findIndex((q) => q.proposal.id === active.proposal.id) : 0;
  return {
    ...base,
    queue,
    active,
    activeIndex: Math.max(0, activeIndex),
    approve: (content?: string) => (active ? base.approveById(active.proposal.id, content) : Promise.resolve()),
    reject: () => (active ? base.rejectById(active.proposal.id) : Promise.resolve()),
    revise: (guidance: string) => {
      if (active) base.revise(guidance);
    },
    select: (index: number) => {
      const item = queue[index];
      if (item) base.selectById(item.proposal.id);
    },
    next: () => {
      const item = queue[Math.min(activeIndex + 1, queue.length - 1)];
      if (item) base.selectById(item.proposal.id);
    },
    prev: () => {
      const item = queue[Math.max(activeIndex - 1, 0)];
      if (item) base.selectById(item.proposal.id);
    },
  };
}

/** The server's structured refusal code (client.ts request() sets ``detail``). */
function decideErrorCode(err: unknown): string | undefined {
  const detail = (err as { detail?: unknown } | null)?.detail;
  const code = detail && typeof detail === "object" ? (detail as { error?: unknown }).error : undefined;
  if (typeof code === "string") return code;
  const msg = err instanceof Error ? err.message : String(err);
  if (/already_decided/.test(msg)) return "already_decided";
  return /version_conflict/.test(msg) ? "version_conflict" : undefined;
}

/** A save landing on the same section at the same moment clears on its own —
 *  retry the decision once before asking the writer to. */
async function withConflictRetry<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (err) {
    if (decideErrorCode(err) !== "version_conflict") throw err;
    await new Promise((r) => setTimeout(r, 600));
    return call();
  }
}

/** Shown when the card was already approved, rejected or replaced elsewhere
 *  (the other review window, or a newer fix pass). */
const ALREADY_DECIDED_MESSAGE = "That change was already handled elsewhere, so it was removed from the list.";

export function useProposalReviewFlow(params: UseProposalReviewFlowParams): UseProposalReviewFlowResult {
  const { runId, refreshSignal, libraryId, onDecided, onError, writeTitleFields, include, reloadSectionInWord } =
    params;
  const includeRef = useRef(include);
  includeRef.current = include;
  const [phase, setPhase] = useState<ProposalReviewPhase>("idle");
  const [queue, setQueue] = useState<ProposalQueueItem[]>([]);
  // The active card by id — stable across re-reads that add or reorder cards.
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newCount, setNewCount] = useState(0);
  const [lastApplied, setLastApplied] = useState<AppliedChange | null>(null);
  const queueRef = useRef<ProposalQueueItem[]>([]);
  queueRef.current = queue;
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Guards async settles against a superseded proposal / stale stream.
  const flowTokenRef = useRef(0);
  const requestKeyRef = useRef("");

  // Writer-facing errors show on the review band itself (``error``) — the
  // host's own error line sits below the card, out of view while reviewing.
  const report = useCallback(
    (message: string) => {
      setError(message);
      onError?.(message);
    },
    [onError],
  );

  const reload = useCallback(async () => {
    if (!runId) {
      setQueue([]);
      setPhase("idle");
      return;
    }
    const key = `${runId}`;
    requestKeyRef.current = key;
    // A re-read with cards on screen happens in the background: the writer keeps
    // reviewing (and their card) while it runs.
    const background = queueRef.current.length > 0;
    if (!background) setPhase("loading");
    setError(null);
    try {
      const resp = await getRunProposals(runId, "pending");
      if (requestKeyRef.current !== key) return;
      const items = flattenQueue(resp.sections, includeRef.current);
      if (background) {
        const seen = new Set(queueRef.current.map((q) => q.proposal.id));
        const added = items.filter((q) => !seen.has(q.proposal.id)).length;
        if (added > 0) setNewCount((n) => n + added);
      }
      setQueue(items);
      setActiveId((cur) =>
        cur && items.some((q) => q.proposal.id === cur) ? cur : (items[0]?.proposal.id ?? null),
      );
      // Never cut an Approve / Revise in flight short.
      setPhase((cur) => (cur === "applying" || cur === "revising" ? cur : items.length > 0 ? "reviewing" : "idle"));
    } catch (err) {
      if (requestKeyRef.current !== key) return;
      if (!background) setPhase("idle");
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [runId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Re-fetch when the shell signals the queue changed (a pass produced a
  // proposal / finished). Skips the mount value — reload's own effect fetched.
  const refreshSeenRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshSignal is the external re-fetch trigger
  useEffect(() => {
    if (!refreshSeenRef.current) {
      refreshSeenRef.current = true;
      return;
    }
    void reload();
  }, [refreshSignal, reload]);

  const activeIndex = useMemo(() => {
    const i = queue.findIndex((q) => q.proposal.id === activeId);
    return i >= 0 ? i : 0;
  }, [queue, activeId]);
  const active = queue[activeIndex] ?? null;

  // Reveal in Word: whenever the active proposal changes (mount, next/prev,
  // select), scroll the open document to the paragraph the change would land in
  // — WITHOUT modifying it. Best-effort: a failure to locate is silent.
  const revealedKeyRef = useRef<string>("");
  useEffect(() => {
    if (!active || (phase !== "reviewing" && phase !== "revising")) return;
    const key = `${active.proposal.id}:${active.proposal.revision_count}`;
    if (revealedKeyRef.current === key) return;
    revealedKeyRef.current = key;
    const { proposal, sectionNumber } = active;
    void (async () => {
      try {
        const marker = proposal.marker_texts[0];
        if (marker && (await selectMarkerParagraph(marker))) return;
        if (await selectSpanInSection(sectionNumber, proposal.original_text)) return;
        // The open document may not carry the [REGULATOR FLAG: …] markers the
        // stored paragraph has (re-projecting them into Word is lossy) — find the
        // paragraph by its prose instead.
        const prose = stripRegulatorMarkers(proposal.original_text);
        if (prose !== proposal.original_text) await selectSpanInSection(sectionNumber, prose);
      } catch {
        // Reveal is a courtesy — never block the review on it.
      }
    })();
  }, [active, phase]);

  /** Take a decided card off the queue. When it was the active one, the card
   *  that slid into its place becomes active. */
  const dropById = useCallback(
    (proposalId: string) => {
      const prevQueue = queueRef.current;
      const idx = prevQueue.findIndex((q) => q.proposal.id === proposalId);
      const nextQueue = prevQueue.filter((q) => q.proposal.id !== proposalId);
      setQueue(nextQueue);
      if (activeId === proposalId || !nextQueue.some((q) => q.proposal.id === activeId)) {
        const landing = nextQueue[Math.max(0, Math.min(idx, nextQueue.length - 1))];
        setActiveId(landing?.proposal.id ?? null);
      }
      setPhase(nextQueue.length > 0 ? "reviewing" : "idle");
    },
    [activeId],
  );

  const moveTo = useCallback((index: number) => {
    const item = queueRef.current[index];
    if (!item) return false;
    setActiveId(item.proposal.id);
    setNewCount(0);
    return true;
  }, []);
  const next = useCallback(() => {
    moveTo(Math.min(activeIndex + 1, Math.max(0, queue.length - 1)));
  }, [moveTo, activeIndex, queue.length]);
  const prev = useCallback(() => {
    moveTo(Math.max(0, activeIndex - 1));
  }, [moveTo, activeIndex]);
  const select = useCallback((index: number) => void moveTo(index), [moveTo]);
  const selectById = useCallback(
    (proposalId: string) => moveTo(queue.findIndex((item) => item.proposal.id === proposalId)),
    [moveTo, queue],
  );

  // The undo offer lapses on its own.
  useEffect(() => {
    if (!lastApplied) return;
    const t = setTimeout(() => setLastApplied(null), UNDO_MS);
    return () => clearTimeout(t);
  }, [lastApplied]);

  const approveItem = useCallback(async (item: ProposalQueueItem | null, content?: string) => {
    if (!runId || !item || phase !== "reviewing") return;
    const token = ++flowTokenRef.current;
    const { proposal, sectionNumber } = item;
    setPhase("applying");
    setError(null);
    setLastApplied(null);
    let applied: AppliedChange | null = null;
    try {
      const edited = content !== undefined && content !== proposal.proposed_text ? content : undefined;
      const decide = () =>
        withConflictRetry(() =>
          edited === undefined
            ? decideProposal(runId, proposal.id, "approve")
            : decideProposal(runId, proposal.id, "approve", edited),
        );
      const tableWrites = tableWritesFromProposal(proposal);
      let missed = false;
      // 1. Server applies to the SSOT + reconciles the ledger (version-checked).
      //    For a table this splices each rewritten row back by index (drift-proof).
      // 2. Mirror the approved change into the open Word document. The SSOT is
      //    already the source of truth; a Word miss does not roll back the
      //    record — the section is re-rendered from it instead.
      // Body changes hold the Word write queue across BOTH steps: an auto-save
      // capture that read Word between them saved the pre-approval paragraph
      // back over the approved one and reopened its flags. The title page is
      // never auto-saved, and its writer queues itself.
      const mirrorBody = async () => {
        if (tableWrites.length > 0) {
          // Table proposal: write only the changed cells in place — never a
          // paragraph splice (which would corrupt the table). Row indices are the
          // Word getCell indices the server computed.
          const result = await applyTableRowEditsInSection(sectionNumber, tableWrites);
          missed = result === "not_found";
          return;
        }
        const cleaned = sanitizeDraftForWord(edited ?? proposal.proposed_text);
        const styled = needsFormatterRender(cleaned);
        // No prefix hint: first-occurrence replace, matching the server's
        // first-occurrence replace_span_in_draft so Word and the SSOT agree.
        const replace = (original: string) =>
          styled
            ? replaceTextInSectionStyled(sectionNumber, original, cleaned)
            : replaceTextInSection(sectionNumber, original, cleaned);
        let result = await replace(proposal.original_text);
        // Word may lack the paragraph's [REGULATOR FLAG: …] markers (see reveal).
        const prose = stripRegulatorMarkers(proposal.original_text);
        if (result === "not_found" && prose !== proposal.original_text) result = await replace(prose);
        missed = result === "not_found" || result === "table_conflict";
        if (!missed) {
          applied = {
            proposalId: proposal.id,
            moduleId: proposal.module_id,
            sectionNumber,
            appliedText: cleaned,
            originalText: proposal.original_text,
          };
        }
      };
      if (isTitleFieldProposal(proposal)) {
        const resp = await decide();
        // Title-page field: the server returns the merged fields — rewrite the
        // cover from them (never a paragraph splice into the body).
        const fields = resp?.title_fields;
        if (fields && Object.keys(fields).length > 0) {
          try {
            await (writeTitleFields ?? ((f: Record<string, string>) => updateTitlePageFields(f)))(fields);
          } catch {
            missed = true;
          }
        } else {
          missed = true;
        }
      } else {
        await enqueueWordWrite(async () => {
          await decide();
          await mirrorBody();
        }, "Applying the approved change");
      }
      if (flowTokenRef.current !== token) return;
      if (missed && !isTitleFieldProposal(proposal) && reloadSectionInWord) {
        // The record has the change but the paragraph wasn't found in Word (it
        // was edited, or Word's text differs from the record's): put the whole
        // section in from the record. Outside the write queue — it queues itself.
        void reloadSectionInWord(proposal.module_id);
      } else if (missed) {
        report(
          isTitleFieldProposal(proposal)
            ? "Applied to the record, but couldn't update the title page in the open document. Load the document to reconcile."
            : `Applied to the record, but couldn't update the open document in §${sectionNumber} — it may have been edited. Load the section to reconcile.`,
        );
      }
      dropById(proposal.id);
      // Assigned inside ``mirrorBody`` — TS can't see that, so widen it back.
      const appliedChange = applied as AppliedChange | null;
      if (appliedChange) setLastApplied(appliedChange);
      onDecided?.();
    } catch (err) {
      if (flowTokenRef.current !== token) return;
      setPhase("reviewing");
      // A 409 span_drift means the section changed since the pass — refresh the
      // queue. Key off the structured status/detail (client.ts request() sets
      // err.status + err.detail), not the human message string.
      const e = err as { status?: number; detail?: { error?: string }; message?: string };
      const detail = err instanceof Error ? err.message : String(err);
      const code = decideErrorCode(err);
      const isConflict = code === "version_conflict";
      const isDrift =
        !isConflict &&
        code !== "already_decided" &&
        (e?.status === 409 || code === "span_drift" || /span_drift|409/.test(detail));
      if (code === "already_decided") {
        // Not a drift: the card is stale. Drop it and move on.
        dropById(proposal.id);
        void reload();
        onDecided?.();
        report(ALREADY_DECIDED_MESSAGE);
      } else if (isConflict) {
        report("The section was being saved at the same moment — press Approve again.");
      } else if (isDrift) {
        // Reloading brings this same proposal back (it is still pending), so
        // say what the writer can do with it rather than "refreshing". After
        // the reload starts: it clears ``error`` synchronously.
        void reload();
        report(
          "This paragraph has changed in the document since the fix was prepared, so it can't be applied as is. Revise it or reject it.",
        );
      } else {
        report(`Couldn't apply the change: ${detail}`);
      }
    }
  }, [runId, phase, dropById, onDecided, report, reload, writeTitleFields, reloadSectionInWord]);

  const rejectItem = useCallback(async (item: ProposalQueueItem | null) => {
    if (!runId || !item || phase !== "reviewing") return;
    const token = ++flowTokenRef.current;
    const { proposal } = item;
    setPhase("applying");
    setError(null);
    try {
      await withConflictRetry(() => decideProposal(runId, proposal.id, "reject"));
      if (flowTokenRef.current !== token) return;
      dropById(proposal.id);
      onDecided?.();
    } catch (err) {
      if (flowTokenRef.current !== token) return;
      setPhase("reviewing");
      if (decideErrorCode(err) === "already_decided") {
        dropById(proposal.id);
        void reload();
        onDecided?.();
        report(ALREADY_DECIDED_MESSAGE);
        return;
      }
      report(`Couldn't reject the change: ${err instanceof Error ? err.message : String(err)}`);
    }
  }, [runId, phase, dropById, onDecided, report, reload]);

  const findItem = useCallback(
    (proposalId: string) => queueRef.current.find((q) => q.proposal.id === proposalId) ?? null,
    [],
  );
  const approve = useCallback((content?: string) => approveItem(active, content), [approveItem, active]);
  const reject = useCallback(() => rejectItem(active), [rejectItem, active]);
  const approveById = useCallback(
    (proposalId: string, content?: string) => approveItem(findItem(proposalId), content),
    [approveItem, findItem],
  );
  const rejectById = useCallback((proposalId: string) => rejectItem(findItem(proposalId)), [rejectItem, findItem]);

  const dismissUndo = useCallback(() => setLastApplied(null), []);
  const undoLast = useCallback(async () => {
    const change = lastApplied;
    if (!runId || !change) return;
    setLastApplied(null);
    setError(null);
    try {
      // Same queue hold as approve: no auto-save may read Word between the
      // record going back and the paragraph going back.
      const result = await enqueueWordWrite(async () => {
        await undoProposal(runId, change.proposalId);
        // Mirror the swap into Word: the applied paragraph back to the original.
        const original = sanitizeDraftForWord(change.originalText);
        return needsFormatterRender(original)
          ? replaceTextInSectionStyled(change.sectionNumber, change.appliedText, original)
          : replaceTextInSection(change.sectionNumber, change.appliedText, original);
      }, "Undoing the change");
      if ((result === "not_found" || result === "table_conflict") && reloadSectionInWord) {
        void reloadSectionInWord(change.moduleId);
      } else if (result === "not_found" || result === "table_conflict") {
        report(
          `Undone in the record, but couldn't update the open document in §${change.sectionNumber} — Load the section to reconcile.`,
        );
      }
      await reload();
      onDecided?.();
    } catch (err) {
      const code = decideErrorCode(err);
      report(
        code === "span_drift"
          ? "That paragraph has changed since it was approved, so it can't be undone here."
          : `Couldn't undo the change: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }, [runId, lastApplied, report, reload, onDecided, reloadSectionInWord]);

  const revise = useCallback(
    (guidance: string) => {
      if (!runId || !active || phase !== "reviewing") return;
      const trimmed = guidance.trim();
      if (!trimmed) return;
      const token = ++flowTokenRef.current;
      const proposalId = active.proposal.id;
      const scope: ResolveIssueDoc[] = [];
      setPhase("revising");
      setProgressLabel(null);
      setError(null);
      let newProposal: SectionProposal | null = null;
      let noChange = false;
      let noChangeReason = "";
      void reviseProposalStream(
        runId,
        proposalId,
        { guidance: trimmed, library_id: libraryId ?? undefined, document_scope: scope },
        (event) => {
          const type = String(event.type ?? "");
          if (type === "complete" && event.no_change) {
            noChange = true;
            noChangeReason = String(event.reason ?? "");
          } else if (type === "complete") {
            newProposal = {
              ...active.proposal,
              id: String(event.proposal_id ?? proposalId),
              module_id: String(event.module_id ?? active.proposal.module_id),
              original_text: String(event.original_text ?? active.proposal.original_text),
              proposed_text: String(event.proposed_text ?? ""),
              issue_ids: active.proposal.issue_ids,
              marker_texts: active.proposal.marker_texts,
              status: "pending",
              computed_section_version: Number(event.section_version ?? active.proposal.computed_section_version),
              revision_count: Number(event.revision_count ?? active.proposal.revision_count + 1),
              supersedes_id: proposalId,
              unfixed_issue_ids: Array.isArray(event.unfixed_issue_ids)
                ? (event.unfixed_issue_ids as unknown[]).map(String)
                : active.proposal.unfixed_issue_ids,
            };
          } else if (type !== "error" && flowTokenRef.current === token) {
            const label = labelForSelectionEditEvent(event, "Rewriting the paragraph…");
            if (label) setProgressLabel(label);
          }
        },
      ).then(
        () => {
          if (flowTokenRef.current !== token) return;
          setProgressLabel(null);
          if (newProposal) {
            const replacement = newProposal;
            setQueue((prev) =>
              prev.map((item) => (item.proposal.id === proposalId ? { ...item, proposal: replacement } : item)),
            );
            setActiveId(replacement.id);
            setPhase("reviewing");
          } else if (noChange) {
            setPhase("reviewing");
            report(
              noChangeReason === "still_flagged"
                ? "The revision still didn't fill the flag, so the current proposal was kept."
                : "The revision came back identical to the original text, so the current proposal was kept.",
            );
          } else {
            setPhase("reviewing");
            report("The rewrite ended without a result — please try again.");
          }
        },
        (err) => {
          if (flowTokenRef.current !== token) return;
          setProgressLabel(null);
          setPhase("reviewing");
          report(`Couldn't revise the rewrite: ${err instanceof Error ? err.message : String(err)}`);
        },
      );
    },
    [runId, active, phase, libraryId, report],
  );

  return {
    phase,
    queue,
    activeIndex,
    active,
    progressLabel,
    error,
    reload,
    next,
    prev,
    select,
    selectById,
    approve,
    reject,
    revise,
    approveById,
    rejectById,
    newCount,
    lastApplied,
    undoLast,
    dismissUndo,
  };
}
